import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faEye, faSpinner, faPlus, faStar } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import api from '../utils/api';
import PropTypes from 'prop-types';

const ProjectManagement = ({ darkMode, onEditProject }) => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteConfirmation, setDeleteConfirmation] = useState({
        show: false,
        projectId: null,
        projectTitle: '',
        isDeleting: false
    });

    useEffect(() => {
        fetchUserProjects();
    }, []);

    const fetchUserProjects = async () => {
        try {
            setLoading(true);
            const response = await api.get('/projects/author/projects');
            
            if (response.data.success) {
                setProjects(response.data.projects);
            }
        } catch (error) {
            console.error('Error fetching user projects:', error);
            toast.error('Failed to load your projects');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteProject = async (projectId) => {
        const project = projects.find(p => p._id === projectId);
        setDeleteConfirmation({
            show: true,
            projectId,
            projectTitle: project.title,
            isDeleting: false
        });
    };

    const confirmDelete = async () => {
        setDeleteConfirmation(prev => ({ ...prev, isDeleting: true }));
        try {
            await api.delete(`/projects/${deleteConfirmation.projectId}`);
            setProjects(prev => prev.filter(project => project._id !== deleteConfirmation.projectId));
            setDeleteConfirmation({ show: false, projectId: null, projectTitle: '', isDeleting: false });
            toast.success('Project deleted successfully!');
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Failed to delete project');
            setDeleteConfirmation(prev => ({ ...prev, isDeleting: false }));
        }
    };

    const handleViewProject = (projectId) => {
        // You can implement project detail view or redirect to project page
        window.open(`/projects/${projectId}`, '_blank');
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const truncateText = (text, maxLength) => {
        if (!text || typeof text !== 'string') return '';
        if (text.length <= maxLength) return text;
        return text.substr(0, maxLength).trim() + '...';
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Active':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'Completed':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'In Progress':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            case 'Archived':
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h2 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Manage Projects
                </h2>
            </div>

            {/* Content Container */}
            <div className={`rounded-2xl border p-6 ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } shadow-lg`}>
                <div className="space-y-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl mr-3" />
                            <span>Loading your projects...</span>
                        </div>
                    ) : projects.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="mb-4">
                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium mb-2">No projects yet</h3>
                            <p className="text-gray-500 mb-4">Create your first project to get started!</p>
                            <button
                                onClick={() => {
                                    // Trigger create project
                                }}
                                className={`px-4 py-2 rounded-lg font-medium text-white ${
                                    darkMode 
                                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500' 
                                        : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600'
                                } transition-all flex items-center gap-2`}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                Create Project
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {projects.map((project) => (
                                <div
                                    key={project._id}
                                    className={`rounded-lg border ${
                                        darkMode 
                                            ? 'bg-gray-700/50 border-gray-600' 
                                            : 'bg-white border-gray-200'
                                    } hover:shadow-lg transition-shadow overflow-hidden`}
                                >
                                    {/* Project Image */}
                                    <div className="relative h-48 overflow-hidden">
                                        <img 
                                            src={project.imageUrl} 
                                            alt={project.title}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.src = 'https://via.placeholder.com/400x200?text=No+Image';
                                            }}
                                        />
                                        {project.featured && (
                                            <div className="absolute top-2 right-2">
                                                <span className="bg-yellow-500 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                                                    <FontAwesomeIcon icon={faStar} />
                                                    Featured
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Project Info */}
                                    <div className="p-4">
                                        <div className="flex items-start justify-between mb-2">
                                            <h3 className="text-lg font-semibold line-clamp-1">{project.title}</h3>
                                            <div className="flex items-center gap-1">
                                                <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(project.status)}`}>
                                                    {project.status}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <p className="text-gray-600 dark:text-gray-300 mb-3 text-sm line-clamp-2">
                                            {truncateText(project.description, 100)}
                                        </p>

                                        <div className="mb-3">
                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">
                                                Technologies:
                                            </span>
                                            <div className="flex flex-wrap gap-1">
                                                {project.technologies.slice(0, 3).map((tech, index) => (
                                                    <span 
                                                        key={index}
                                                        className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded"
                                                    >
                                                        {tech}
                                                    </span>
                                                ))}
                                                {project.technologies.length > 3 && (
                                                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded">
                                                        +{project.technologies.length - 3} more
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                                            <span>{project.type}</span>
                                            <span>{formatDate(project.createdAt)}</span>
                                        </div>

                                        <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                                            <span>{project.views} views</span>
                                            <span>{project.likes} likes</span>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleViewProject(project._id)}
                                                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${
                                                    darkMode 
                                                        ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                                                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                                                } transition-colors flex items-center justify-center gap-1`}
                                            >
                                                <FontAwesomeIcon icon={faEye} />
                                                View
                                            </button>
                                            <button
                                                onClick={() => onEditProject && onEditProject(project)}
                                                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${
                                                    darkMode 
                                                        ? 'bg-green-600 hover:bg-green-700 text-white' 
                                                        : 'bg-green-500 hover:bg-green-600 text-white'
                                                } transition-colors flex items-center justify-center gap-1`}
                                            >
                                                <FontAwesomeIcon icon={faEdit} />
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDeleteProject(project._id)}
                                                className={`px-3 py-2 rounded-lg text-sm font-medium ${
                                                    darkMode 
                                                        ? 'bg-red-600 hover:bg-red-700 text-white' 
                                                        : 'bg-red-500 hover:bg-red-600 text-white'
                                                } transition-colors flex items-center justify-center gap-1`}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {deleteConfirmation.show && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className={`p-6 rounded-2xl shadow-xl max-w-md w-full border ${
                        darkMode ? 'bg-gray-800/95 border-gray-700' : 'bg-white/95 backdrop-blur-md border-gray-200'
                    }`}>
                        <h3 className="text-2xl font-semibold mb-2">Confirm Delete</h3>
                        <p className="mb-2">Are you sure you want to delete this project?</p>
                        <p className="mb-6 font-semibold">&quot;{deleteConfirmation.projectTitle}&quot;</p>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setDeleteConfirmation({ show: false, projectId: null, projectTitle: '', isDeleting: false })}
                                className={`px-4 py-2 rounded ${
                                    darkMode ? 'bg-gray-600 hover:bg-gray-700 text-white' : 'bg-gray-300 hover:bg-gray-400 text-gray-800'
                                }`}
                                disabled={deleteConfirmation.isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                disabled={deleteConfirmation.isDeleting}
                                className={`px-4 py-2 rounded text-white ${
                                    darkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600'
                                } disabled:bg-red-400 disabled:cursor-not-allowed flex items-center gap-2`}
                            >
                                {deleteConfirmation.isDeleting && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                                {deleteConfirmation.isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

ProjectManagement.propTypes = {
    darkMode: PropTypes.bool.isRequired,
    onEditProject: PropTypes.func
};

export default ProjectManagement;
